import React from 'react';
import {
  Headphones,
  Mail,
  ExternalLink,
  AlertCircle,
  Monitor,
  Laptop,
  Wrench,
  Settings,
  Download,
  CheckCircle,
  Clock,
  MessageSquare,
} from 'lucide-react';
import SectionHeader from '../ui/SectionHeader';

const commonIssues = [
  {
    id: 'fallos-fisicos',
    icon: Monitor,
    title: 'Fallos físicos de ordenador',
    description: 'Problemas con hardware, pantalla, teclado o ratón',
  },
  {
    id: 'software-no-funciona',
    icon: Laptop,
    title: 'Software que no funciona',
    description: 'Aplicaciones que no responden o se cierran inesperadamente',
  },
  {
    id: 'dificultades-aplicaciones',
    icon: Wrench,
    title: 'Dificultades con aplicaciones',
    description: 'Errores o problemas al usar programas específicos',
  },
  {
    id: 'configuracion-programas',
    icon: Settings,
    title: 'Configuración de programas',
    description: 'Ayuda para configurar software o cuentas',
  },
  {
    id: 'instalacion-software',
    icon: Download,
    title: 'Instalación de nuevo software',
    description: 'Solicitud de instalación de nuevas aplicaciones',
  },
];

const SupportPage = () => {
  return (
    <div className="animate-stagger">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary mb-2">Soporte Técnico</h1>
        <p className="text-text-secondary">
          Centro de ayuda y soporte técnico de Sonneil
        </p>
      </div>

      {/* Main Banner */}
      <section id="notificar-incidencia" className="section scroll-mt-24">
        <div className="card overflow-hidden">
          <div className="bg-gradient-to-r from-primary to-primary-dark p-8 text-white">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <Headphones size={32} />
              </div>
              <div>
                <h2 className="text-2xl font-bold">¿Cómo notificar una incidencia al equipo de IT?</h2>
                <div className="flex items-center gap-2 mt-2">
                  <CheckCircle size={16} />
                  <span className="text-white/80">Servicio operativo</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-8">
            <div className="prose max-w-none">
              <p className="text-text-secondary text-lg leading-relaxed mb-6">
                Si experimentas problemas con tu ordenador, ya sea un fallo físico, un software 
                que no funciona correctamente, dificultades con una aplicación, o necesitas ayuda 
                para configurar o instalar un nuevo programa, escríbenos al correo{' '}
                <strong className="text-primary">Tech Support (techsupport@sonneil.com)</strong>.
              </p>
              <p className="text-text-secondary leading-relaxed">
                Ahí se genera un ticket que será atendido lo más pronto posible por nuestro equipo técnico.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Options */}
      <section className="section">
        <SectionHeader
          title="Opciones de Contacto"
          description="Elige la forma más conveniente para contactar con soporte"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Email Support */}
          <a
            id="soporte-email"
            href="mailto:techsupport@sonneil.com"
            className="card p-6 flex items-start gap-4 hover:border-primary/30 border border-transparent transition-all group scroll-mt-24"
          >
            <div className="w-14 h-14 rounded-xl bg-primary-lighter/50 flex items-center justify-center flex-shrink-0">
              <Mail size={28} className="text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-text-primary group-hover:text-primary transition-colors mb-1">
                Enviar Email
              </h3>
              <p className="text-primary font-medium mb-2">techsupport@sonneil.com</p>
              <p className="text-sm text-text-tertiary">
                Se creará automáticamente un ticket de soporte
              </p>
              <div className="flex items-center gap-2 mt-3 text-sm text-text-secondary">
                <Clock size={14} />
                <span>Respuesta en menos de 24h</span>
              </div>
            </div>
          </a>

          {/* Help Center */}
          <a
            id="help-center"
            href="https://help.sonneil.com"
            target="_blank"
            rel="noopener noreferrer"
            className="card p-6 flex items-start gap-4 hover:border-primary/30 border border-transparent transition-all group scroll-mt-24"
          >
            <div className="w-14 h-14 rounded-xl bg-info/10 flex items-center justify-center flex-shrink-0">
              <ExternalLink size={28} className="text-info" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-text-primary group-hover:text-primary transition-colors mb-1">
                Sonneil Help Center
              </h3>
              <p className="text-info font-medium mb-2">help.sonneil.com</p>
              <p className="text-sm text-text-tertiary">
                FAQs, documentación técnica e instrucciones
              </p>
              <div className="flex items-center gap-2 mt-3 text-sm text-text-secondary">
                <MessageSquare size={14} />
                <span>Autoservicio 24/7</span>
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* Common Issues */}
      <section className="section">
        <SectionHeader
          title="Tipos de Soporte"
          description="Problemas frecuentes que podemos ayudarte a resolver"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {commonIssues.map((issue) => {
            const Icon = issue.icon;
            return (
              <div
                key={issue.id}
                id={issue.id}
                className="card p-5 flex items-start gap-4 scroll-mt-24"
              >
                <div className="w-10 h-10 rounded-lg bg-background flex items-center justify-center flex-shrink-0">
                  <Icon size={20} className="text-text-secondary" />
                </div>
                <div>
                  <h4 className="font-medium text-text-primary mb-1">{issue.title}</h4>
                  <p className="text-sm text-text-tertiary">{issue.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Tips Section */}
      <section className="mt-8 p-6 bg-warning/5 rounded-2xl border border-warning/20">
        <div className="flex items-start gap-4">
          <AlertCircle size={24} className="text-warning flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-text-primary mb-2">
              Consejos para un soporte más rápido
            </h3>
            <ul className="text-text-secondary text-sm space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-warning mt-1">•</span>
                <span>
                  <strong>Describe el problema claramente:</strong> Incluye qué estabas haciendo cuando ocurrió el error
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-warning mt-1">•</span>
                <span>
                  <strong>Incluye capturas de pantalla:</strong> Si ves un mensaje de error, adjunta una imagen
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-warning mt-1">•</span>
                <span>
                  <strong>Indica la urgencia:</strong> Si el problema bloquea tu trabajo, menciónalo en el asunto
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-warning mt-1">•</span>
                <span>
                  <strong>Proporciona tu disponibilidad:</strong> Para que podamos contactarte si necesitamos más información
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Quick Action */}
      <section className="mt-8 text-center">
        <div className="card p-8 bg-gradient-to-br from-primary-lighter/30 to-background">
          <Headphones size={40} className="mx-auto text-primary mb-4" />
          <h3 className="text-xl font-semibold text-text-primary mb-2">¿Necesitas ayuda ahora?</h3>
          <p className="text-text-secondary mb-6 max-w-md mx-auto">
            Nuestro equipo técnico está listo para ayudarte con cualquier problema
          </p>
          <a
            href="mailto:techsupport@sonneil.com"
            className="btn-primary btn-lg inline-flex"
          >
            <Mail size={20} />
            Contactar Soporte
          </a>
        </div>
      </section>
    </div>
  );
};

export default SupportPage;


